/**
 * Cola de notificaciones del shell.
 *
 * Es un store minimo y no un contexto de React a proposito: `notify()` se puede
 * llamar desde un `catch` de una mutacion o desde un helper que no es componente,
 * sin tener que arrastrar un hook hasta ahi.
 */

import { create } from 'zustand';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastOptions {
  tone?: ToastTone;
  description?: string;
  /** Milisegundos hasta el cierre automatico. `0` deja el aviso fijo. */
  duration?: number;
}

interface ToastState {
  toasts: Toast[];
  push: (title: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

let siguienteId = 1;

/** Los errores no se auto-cierran: si algo fallo, el usuario tiene que poder leerlo. */
const DURACION_POR_TONO: Record<ToastTone, number> = {
  info: 4000,
  success: 3500,
  warning: 6000,
  danger: 0,
};

/** Tope de avisos simultaneos: mas que esto tapa la pantalla en un celular. */
const MAX_VISIBLES = 4;

export const useToastStore = create<ToastState>()((set, get) => ({
  toasts: [],

  push: (title, options = {}) => {
    const id = siguienteId++;
    const tone = options.tone ?? 'info';
    const toast: Toast = { id, tone, title };
    if (options.description !== undefined) toast.description = options.description;

    set((estado) => ({ toasts: [...estado.toasts, toast].slice(-MAX_VISIBLES) }));

    const duracion = options.duration ?? DURACION_POR_TONO[tone];
    if (duracion > 0) {
      setTimeout(() => {
        get().dismiss(id);
      }, duracion);
    }
    return id;
  },

  dismiss: (id) => {
    set((estado) => ({ toasts: estado.toasts.filter((toast) => toast.id !== id) }));
  },

  clear: () => {
    set({ toasts: [] });
  },
}));

/** Atajo para usar fuera de un componente. */
export const notify = {
  info: (title: string, description?: string) =>
    useToastStore.getState().push(title, description === undefined ? {} : { description }),
  success: (title: string, description?: string) =>
    useToastStore
      .getState()
      .push(title, description === undefined ? { tone: 'success' } : { tone: 'success', description }),
  warning: (title: string, description?: string) =>
    useToastStore
      .getState()
      .push(title, description === undefined ? { tone: 'warning' } : { tone: 'warning', description }),
  error: (title: string, description?: string) =>
    useToastStore
      .getState()
      .push(title, description === undefined ? { tone: 'danger' } : { tone: 'danger', description }),
};
